import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-regular-svg-icons'
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import { UserAuth } from '../../authentication/AuthContext';
import { isCurrentPassword } from '../../authentication/Firebase';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../../components/button/Button.jsx';
import { Input, Password } from '../../components/form/Input.jsx';
import SpinnerIcon from '../../assets/common/spinner.svg?react';
import ErrorIcon from '../../assets/common/error-octagon.svg?react';
import SuccessIcon from '../../assets/common/check-circle.svg?react';
import CircleCheckIcon from '../../assets/common/check-circle.svg?react';

import './FirebasePages.css';

const ResetPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { verifyResetCode, confirmReset, resetPassword } = UserAuth();
    const hasVerified = useRef(false);

    const [actionCode, setActionCode] = useState('');
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [resetting, setResetting] = useState(false);
    const [status, setStatus] = useState('verifying'); // verifying, actionCodeError, resetForm, success, error
    const [resendTimer, setResendTimer] = useState(undefined);
    const [emailError, setEmailError] = useState('');
    const [isResending, setIsResending] = useState(false);

    const passwordValidations = [
        {
            text: "8-16 characters",
            check: (pw) => pw.length >= 8 && pw.length <= 16
        },
        {
            text: "1 uppercase letter",
            check: (pw) => /[A-Z]/.test(pw)
        },
        {
            text: "1 lowercase letter",
            check: (pw) => /[a-z]/.test(pw)
        },
        {
            text: "1 number",
            check: (pw) => /[0-9]/.test(pw)
        },
        {
            text: "1 special character",
            check: (pw) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pw)
        }
    ];

    const states = {
        verifying: {
            icon: <SpinnerIcon />,
            title: 'Password Reset',
            helpText: 'Verifying reset link. This usually takes just a few seconds. Kindly wait for a while',
            buttonSingleRow: false
        },
        actionCodeError: {
            icon: <ErrorIcon />,
            title: 'Oops! Something Went Wrong',
            helpText: <>We Encountered an issue while processing your password reset request.<br/><br/>Invalid password reset link. No verification code found.<br /><br/>Don’t worry! you can request a new password reset link from the login page, or contact our support team if you continue to experience issues.</>,
            buttonSingleRow: false
        },
        resetForm: {
            icon: null,
            title: 'Reset password',
            helpText: 'Creating new password for account',
            buttonSingleRow: true
        },
        success: {
            icon: <SuccessIcon />,
            title: 'Password reset successful',
            helpText: 'Your password was updated successfully. Sign in now and keep it secure.',
            buttonSingleRow: false
        },
        error: {
            icon: <ErrorIcon />,
            title: 'Oops! Something Went Wrong',
            helpText: 'Invalid reset link. Request a new one from login page.',
            buttonSingleRow: false
        },
    };

    useEffect(() => {
        let timerInterval;
        if (resendTimer && resendTimer > 0) {
            timerInterval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timerInterval);
    }, [resendTimer]);

    useEffect(() => {
        if (hasVerified.current) {
            return;
        }
        hasVerified.current = true;

        const handleVerification = async () => {
            try {
                await new Promise(resolve => setTimeout(resolve, 1000)); //gives a spinning effect for better visualization

                const params = new URLSearchParams(location.search);
                const code = params.get('oobCode');

                if (!code) {
                    setStatus('actionCodeError');
                    return;
                }

                const result = await verifyResetCode(code);
                if (result.success) {
                    setActionCode(code);
                    setEmail(result.email);
                    setStatus('resetForm');
                } else {
                    setStatus('error');
                }
            } catch (error) {
                setStatus('error');
            }
        };

        handleVerification();
    }, [location, verifyResetCode]);

    const isPasswordValid = (password) => {
        return (
            password.length >= 8 &&
            password.length <= 16 &&
            /[A-Z]/.test(password) &&
            /[a-z]/.test(password) &&
            /[0-9]/.test(password) &&
            /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
        );
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (!isPasswordValid(newPassword)) {
            setError('Password does not meet requirements');
            return;
        }

        try {
            setResetting(true);
            setError('');

            if (await isCurrentPassword(email, newPassword)) {
                setError('New password must be different from your current password.');
                return;
            }

            const result = await confirmReset(actionCode, newPassword);
            if (result?.success) {
                setStatus('success');
            } else {
                setStatus('error');
            }
        } catch (err) {
            setStatus('error');
        } finally {
            setResetting(false);
        }
    };

    const validateEmail = (email) => {
        const re = /^[a-zA-Z0-9._%+-]+@(liquidnitrogames\.com|liquidnitro\.games)$/;
        return re.test(email);
    };

    const handleEmailResend = async () => {
        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }
        setIsResending(true);
        try {
            await resetPassword(email);
            setEmailError('');
            setResendTimer(60);
        } catch (error) {
            setEmailError('Failed to resend password reset link. Please try again later.');
        } finally {
            setIsResending(false);
        }
    };

    const handleEmailInputChange = (e) => {
        setEmailError('');
        setEmail(e.target.value);
    };

    const renderIcon = () => {
        if (!states[status].icon) return null;
        return (
            <div className={`firebase-link-page-icon ${status}`}>
                {states[status].icon}
            </div>
        );
    };

    const renderMessage = () => {
        return (
            <div className={`firebase-link-page-message ${status==='resetForm' ? 'left-align' : ''}`}>
                <h2>{states[status].title}</h2>
                <div className="firebase-link-page-message-help-text">
                    {states[status].helpText}
                </div>
            </div>
        );
    };

    const renderExtraContent = () => {
        if (status === 'resetForm') {
            return (
                <div className="firebase-reset-password-extra-content">
                    <Password
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter password"
                        label="New Password:"
                        required
                    />
                    <Password
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-Enter password"
                        label="Confirm Password:"
                        required
                    />
                    <div className="firebase-reset-password-validations">
                        {passwordValidations.map((validation, index) => (
                            <div
                                key={index}
                                className={`firebase-reset-password-validation-item ${validation.check(newPassword) ? 'valid' : 'invalid'}`}
                            >
                                <span>{validation.check(newPassword) ? <CircleCheckIcon/> : <FontAwesomeIcon icon={faXmark} />}</span>
                                {validation.text}
                            </div>
                        ))}
                    </div>
                    {error && <div className="error-message">{error}</div>}
                </div>
            );
        }

        if (status === 'error' || status === 'actionCodeError') {
            return (
                <div className="firebase-reset-password-extra-content">
                    <Input
                        label="Enter Email"
                        value={email}
                        onChange={handleEmailInputChange}
                        placeholder="Enter email"
                        isRequired
                        status={emailError ? 'danger' : 'neutral'}
                        helpText={emailError}
                    />
                    <TertiaryButton>Didn't Find the Link?</TertiaryButton>
                    {resendTimer ? (
                        <p className="firebase-reset-password-timer">
                            <FontAwesomeIcon icon={faClock} /> <span>Try again in</span>
                            <span className="firebase-reset-password-timer-primary">{resendTimer}s</span>
                        </p>
                    ) : null}
                </div>
            );
        }

        return null;
    };

    const renderButtons = () => {
        const buttonConfig = {
            verifying: [],
            actionCodeError: [
                { label: isResending ? 'Resending...' : 'Resend Password Link', onClick: handleEmailResend, disabled: (resendTimer && resendTimer > 0) || isResending, loading: isResending, secondary: true },
                { label: 'Back to Home Page', onClick: () => navigate('/') },
            ],
            resetForm: [
                { label: 'Back to Home Page', onClick: () => navigate('/'), secondary: true },
                { label: resetting ? 'Changing...' : 'Change Password', onClick: handlePasswordReset, disabled: resetting },
            ],
            success: [
                { label: 'Continue to Home Page', onClick: () => navigate('/'), secondary: true }
            ],
            error: [
                { label: isResending ? 'Resending...' : 'Resend Password Link', onClick: handleEmailResend, disabled: (resendTimer && resendTimer > 0) || isResending, loading: isResending, secondary: true },
                { label: 'Back to Home Page', onClick: () => navigate('/') },
            ],
        };

        return (
            <div className={`firebase-reset-password-actions ${states[status].buttonSingleRow ? 'row' : ''}`}>
                {buttonConfig[status].map((btn, index) => (
                    btn.secondary ? (
                        <SecondaryButton
                            key={index}
                            onClick={btn.onClick}
                            disabled={btn.disabled}
                        >
                            {btn.label}
                        </SecondaryButton>
                    ) : (
                        <PrimaryButton
                            key={index}
                            onClick={btn.onClick}
                            disabled={btn.disabled}
                            isEnterTriggered={status === 'resetForm'}
                        >
                            {btn.label}
                        </PrimaryButton>
                    )
                ))}
            </div>
        );
    };

    return (
        <div className="firebase-link-page">
            {renderIcon()}
            {renderMessage()}
            {renderExtraContent()}
            {renderButtons()}
        </div>
    );
};

export default ResetPassword;